import { cloneDeep, debounce, keyBy, noop } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { FOLDER_SEARCH_VISIBLE_RESULTS } from '~/common/constants/max_lengths';
import { flattenFolderTree } from '~/components/SearchV2/SearchV2.utils';
import { Box } from '~/eds/src/components/Box/Box';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { FolderSelect } from '~/eds/src/components/Select/subcomponents/FolderSelect';
import { Folder } from '~/eds/src/types/Folder';
import { withTreeNodeKeyDownHandler } from '~/hocs/withTreeNodeKeyDownHandler';
import { api } from '~/redux/api/createApi';
import { FlattenFolderTree } from '~/types/Automation';
import { FolderTree } from '~/types/FolderTree';
import { ERROR } from '~/types/toast.types';
import { TreeNode } from '~/utils/TreeNode';

import EcFolderTreeItem from '../EcFolderTreeItem/EcFolderTreeItem';
import { showToast } from '../EcToast/showToast';

type Props = {
  folderPathId?: number;
  isRootFolder: boolean;
  onSelect: (folder: Folder) => void;
  disableSyncPaired?: boolean;
  selectedFolder?: Folder;
  shouldDisable?: (folder: FolderTree) => boolean;
  w?: number;
};

const FolderTreeItem = withTreeNodeKeyDownHandler(EcFolderTreeItem);
const FolderPanel = ({
  folderPathId,
  isRootFolder,
  onSelect,
  disableSyncPaired,
  selectedFolder,
  shouldDisable = () => false,
  w,
}: Props) => {
  const intl = useIntl();
  const [folderSearch, onFolderSearch] = useState<string>('');
  const {
    data: folderTreeData,
    isFetching,
    isError,
  } = api.endpoints.getFolderTree.useQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const folderTree = useMemo(() => {
    return folderTreeData?.id ? new TreeNode(cloneDeep(folderTreeData)) : null;
  }, [folderTreeData]);

  const searchFolders = useCallback(
    (folder: Folder) => {
      return (
        folder.name
          .toLocaleLowerCase()
          .indexOf(folderSearch.toLocaleLowerCase()) >= 0
      );
    },
    [folderSearch],
  );

  const flatFolderList = useMemo(() => {
    return folderTreeData
      ? flattenFolderTree([folderTreeData], '', shouldDisable).map(asFolderItem)
      : [];
  }, [folderTreeData, shouldDisable]);

  const folderMap = useMemo(() => {
    return keyBy(flatFolderList, 'id');
  }, [flatFolderList]);

  const folderList = useMemo(() => {
    return folderSearch
      ? flatFolderList
          .filter(searchFolders)
          .slice(0, FOLDER_SEARCH_VISIBLE_RESULTS)
      : [];
  }, [flatFolderList, folderSearch]);

  useEffect(() => {
    if (isError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'FolderPanel.errorMessage',
          defaultMessage: 'Something went wrong, please try again later.',
          description: 'Error message when folder tree fails to load',
        }),
      );
    }
  }, [isError, intl]);

  return (
    <Layout h="100%" direction="column" p={1} w={w}>
      <FolderSelect
        // @ts-ignore missing prop mapping
        isClearable={true}
        isEmbedded={true}
        folders={folderList}
        isMulti={false}
        name="folder filter"
        disabled={isFetching || isError}
        noOptionsMessage={
          folderSearch
            ? intl.formatMessage({
                id: 'FolderPanel.noFoldersMessage',
                defaultMessage: 'No folders match your search',
                description: 'Message when no folders match search criteria',
              })
            : null
        }
        onSearch={debounce(onFolderSearch, 300)}
        onChange={(id) => {
          if (id) {
            onSelect(folderMap[String(id)]);
          }
        }}
      />

      <ContentContainer loadingContent={{ isLoading: isFetching }}>
        <Box h="100%" overflowY="auto">
          {!folderSearch && selectedFolder && (
            <Layout display="flex" direction="row" align="center" pl={4} pt={2}>
              <Box pr={2}>
                {intl.formatMessage({
                  id: 'FolderPanel.selectedFolderLabel',
                  defaultMessage: 'Selected folder: ',
                  description: 'Label for selected folder display',
                })}
              </Box>
              <Chip text={selectedFolder.path || selectedFolder.name} />
            </Layout>
          )}
          {folderTree && !folderSearch && (
            <FolderTreeItem
              autoFocus={true}
              firstToOpenId={
                folderPathId ? Number(folderPathId) : selectedFolder?.id
              }
              folderIdsSelected={[]}
              folder={folderTree}
              isRootFolder={isRootFolder}
              isMultiSelect={false}
              setIsSelected={noop}
              getIsSelected={noop}
              onSelect={(folder: FolderTree) =>
                onSelect(folderMap[String(folder.id)])
              }
              disableSyncPaired={disableSyncPaired}
            />
          )}
        </Box>
      </ContentContainer>
    </Layout>
  );
};

const asFolderItem = (folder: FlattenFolderTree): Folder => {
  return {
    id: folder.value,
    name: folder.display_value,
    path: folder.path,
    disabled: folder.disabled,
  };
};

export default FolderPanel;
